import { datadogRum } from '@datadog/browser-rum';
import { AssessmentEngine, PersonalityAssessmentEngine, type ElementBadge } from '../lib/sdk';
import { ReportProps } from '../ReportProps';
import type { Configuration, ValidatedConfiguration, ICustomColors } from '../ClientConfiguration';
import { consoleErrorEnvironments } from '../ConfigurationValidation';

export const getProductLogoFileName = (productLogo: string, currentLanguage: string): string => {
  const productLogoSplit = productLogo.split('/') as Array<string>;

  return `${currentLanguage}_${productLogoSplit[productLogoSplit.length - 1]}`;
};

export const scrollToTopOfParent = (elementId: string, isAlreadyOpen: boolean): void => {
  setTimeout(() => {
    const elementToScroll = document.getElementById(elementId);

    if (elementToScroll && !isAlreadyOpen) {
      elementToScroll.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, 500);
};

export const openCloseSection = async (
  reportProps: ReportProps,
  elementId: string,
  panelControlType: string,
  isAlreadyOpen: boolean,
): Promise<void> => {
  scrollToTopOfParent(`${panelControlType}-${elementId}`, isAlreadyOpen);
  await reportProps.earnReadBadge(elementId);
};

export const logError = (error: Error, unvalidatedconfiguration: Configuration): void => {
  if (consoleErrorEnvironments.includes(unvalidatedconfiguration.apiEnvironment)) {
    console.error(error);
  }

  if (unvalidatedconfiguration.enableDataDog) {
    datadogRum.addError(error, {});
  }
};

export const reloadOnError = async (
  error: Error,
  assessmentEngine: AssessmentEngine | PersonalityAssessmentEngine,
  configuration: ValidatedConfiguration,
  emit: (event: 'clicked', functionName: string) => void,
): Promise<void> => {
  logError(error, configuration);

  await assessmentEngine.reInitialize();
  emit('clicked', 'reloadPage');
};

// eslint-disable-next-line
export const getGraphIconPosition = (isRTL: boolean, percentPosition: number): number => (isRTL ? 100 - percentPosition : percentPosition);

// Panel titles point aria-describedby at these ids, so the referenced element and the reference must both build
// them here.
export const getGraphDescriptionId = (elementId: string): string => `${elementId}-description`;
export const getBadgesId = (elementId: string): string => `badges-${elementId}`;

export const hasBadges = (isBadgingEnabled: boolean, badges: Array<ElementBadge> | null): boolean => isBadgingEnabled && badges?.length > 0;

export const getGraphAccessibilityProps = (
  ariaLabel: string,
  describedBy: string,
): Record<'role' | 'aria-label' | 'aria-describedby', string> => ({
  role: 'img',
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
});

export const getPrintCustomProperties = (customColors: ICustomColors): string => {
  let styleTag = '<style>.hesAPISPA {';

  for (const [key, value] of Object.entries(customColors)) {
    styleTag += `--hes-color-${key}: ${value};`;
  }

  styleTag += '}</style>';

  return styleTag;
};

export const waitForElementToExist = (selector: string, callback: (element: HTMLElement) => void, timeout = 5000) => {
  const observer = new MutationObserver((mutations, observer) => {
    const element = document.querySelector(selector) as HTMLElement | null;

    if (element) {
      observer.disconnect();
      callback(element);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  if (timeout) {
    setTimeout(() => {
      observer.disconnect();
      console.warn(`Timeout: Element "${selector}" was not found in the DOM.`);
    }, timeout);
  }
};

export const isDevEnvironment = (apiEnvironment: string): boolean => {
  const devEnvironments = ['ci', 'localvue', 'preview', 'local'];

  return devEnvironments.includes(apiEnvironment);
};

export const findFormattedDate = (date: string | null, locale: string, timezone: string): string | null => {
  if (date === null) {
    return null;
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  try {
    return parsedDate.toLocaleDateString(locale, { timeZone: timezone });
  } catch {
    return parsedDate.toLocaleDateString(locale);
  }
};

// `|| undefined`, not '': Vue renders an empty string as aria-describedby="", but omits the attribute for undefined.
export const findAriaIdList = (ids: Array<string | false | null | undefined>): string | undefined =>
  ids.filter(Boolean).join(' ') || undefined;
